/**
 * Token Refresh Manager
 * WP-AUTH-REFRESH-DEDUP-1: Layer 1 - Single-tab promise deduplication
 * WP-BUG-51: Layer 1.5 - 429 retry with exponential backoff
 *
 * Problem: Multiple code paths can trigger concurrent /auth/refresh requests.
 * Solution: Cache the in-flight promise and return it to all concurrent callers.
 *
 * WP-BUG-51: When /auth/refresh returns 429 (rate limited), retry up to 3 times
 * with exponential backoff instead of propagating the error immediately.
 * The retry promise is shared via dedup, so concurrent callers don't each retry.
 *
 * CRITICAL: Uses rawRefreshClient to bypass the 401 interceptor. Using apiClient
 * would cause redirect to /login on failure instead of letting errors propagate.
 */

import { isAxiosError } from 'axios';
import type { AuthTokens } from '@/types';
import { rawRefreshClient } from './api';

/** Maximum number of retry attempts on 429 responses */
export const MAX_REFRESH_RETRIES = 3;

/** Base delay in milliseconds for exponential backoff (2s, 4s, 8s) */
export const BACKOFF_BASE_MS = 2000;

/** In-flight refresh promise - null when no refresh is in progress */
let refreshPromise: Promise<AuthTokens> | null = null;

/** Timestamp of last successful refresh - for debugging/metrics */
let lastRefreshTime: number = 0;

/** Counter for concurrent callers - for metrics */
let concurrentCallerCount: number = 0;

/**
 * Parse Retry-After header value from response headers.
 * Returns delay in milliseconds, or null if header is missing/invalid.
 */
function parseRetryAfter(headers: Record<string, string> | undefined): number | null {
  const value = headers?.['retry-after'];
  if (!value) return null;
  const seconds = parseInt(value, 10);
  return isNaN(seconds) || seconds <= 0 ? null : seconds * 1000;
}

/** Sleep for the specified number of milliseconds */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Attempt a single refresh call with retry logic for 429 responses.
 * WP-BUG-51: Retries up to MAX_REFRESH_RETRIES times on 429, using
 * Retry-After header or exponential backoff.
 *
 * @param retries - Current retry count (0-based)
 * @returns Promise resolving to new auth tokens
 * @throws AxiosError if refresh fails with non-429 error or retries exhausted
 */
async function attemptRefresh(retries = 0): Promise<AuthTokens> {
  try {
    const response = await rawRefreshClient.post<AuthTokens>('/auth/refresh', {});
    lastRefreshTime = Date.now();
    console.debug('[RefreshManager] Refresh successful');
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 429 && retries < MAX_REFRESH_RETRIES) {
      const retryAfter = parseRetryAfter(
        error.response.headers as Record<string, string> | undefined
      );
      const delay = retryAfter ?? BACKOFF_BASE_MS * Math.pow(2, retries);
      console.debug(
        `[RefreshManager] Rate limited, retry ${retries + 1}/${MAX_REFRESH_RETRIES} in ${delay}ms`
      );
      await sleep(delay);
      return attemptRefresh(retries + 1);
    }
    console.error(
      '[RefreshManager] Refresh failed:',
      error instanceof Error ? error.message : 'Unknown error'
    );
    throw error;
  }
}

/**
 * Perform token refresh with single-tab deduplication.
 *
 * If a refresh is already in progress, returns the existing promise.
 * All concurrent callers will receive the same result (success or failure).
 * WP-BUG-51: The shared promise includes retry logic, so concurrent callers
 * wait for retries to complete rather than each retrying independently.
 *
 * @returns Promise resolving to new auth tokens
 * @throws AxiosError if refresh fails (after retries exhausted for 429)
 */
export async function refreshWithDedup(): Promise<AuthTokens> {
  concurrentCallerCount++;

  // If refresh already in progress, return existing promise
  if (refreshPromise !== null) {
    console.debug(
      `[RefreshManager] Returning existing promise (${concurrentCallerCount} concurrent callers)`
    );
    try {
      return await refreshPromise;
    } finally {
      concurrentCallerCount--;
    }
  }

  console.debug('[RefreshManager] Starting new refresh request');

  // Create new refresh promise with retry logic (bypasses 401 interceptor)
  // WP-BUG-51: attemptRefresh handles 429 retries internally
  refreshPromise = attemptRefresh().finally(() => {
    // Clear promise cache so next refresh attempt creates new request
    refreshPromise = null;
  });

  try {
    return await refreshPromise;
  } finally {
    concurrentCallerCount--;
  }
}

/**
 * Check if a refresh is currently in progress.
 */
export function isRefreshInProgress(): boolean {
  return refreshPromise !== null;
}

/**
 * Get time since last successful refresh in milliseconds.
 * Returns -1 if no refresh has occurred.
 */
export function getTimeSinceLastRefresh(): number {
  if (lastRefreshTime === 0) return -1;
  return Date.now() - lastRefreshTime;
}

/**
 * Reset manager state. Only use in tests.
 * @internal
 */
export function _resetForTesting(): void {
  refreshPromise = null;
  lastRefreshTime = 0;
  concurrentCallerCount = 0;
}
